import { useEffect, useRef, useState } from 'react'
import { generateClient, type GraphQLSubscription } from 'aws-amplify/api'
import {
  onBookingChanged,
  onBookingChangedForPrestataire,
  onBookingChangedForClient,
  type BookingTable,
} from '@okeys/shared-utils'

export type SubscriptionType = 'shop' | 'prestataire' | 'client'

export interface UseBookingSubscriptionOptions {
  type: SubscriptionType
  id: string // shopId, prestatereId, or clientId
  enabled?: boolean
  onBookingUpdate?: (booking: BookingTable) => void
  onError?: (error: Error) => void
}

export interface UseBookingSubscriptionReturn {
  lastBooking: BookingTable | null
  isConnected: boolean
  error: Error | null
  reconnect: () => void
}

/**
 * Hook pour écouter les changements de booking en temps réel via AppSync
 *
 * @example
 * ```tsx
 * const { lastBooking, isConnected } = useBookingSubscription({
 *   type: 'shop',
 *   id: shopId,
 *   onBookingUpdate: (booking) => {
 *     console.log('Booking updated:', booking)
 *     queryClient.invalidateQueries(['bookings'])
 *   }
 * })
 * ```
 */
export function useBookingSubscription({
  type,
  id,
  enabled = true,
  onBookingUpdate,
  onError,
}: UseBookingSubscriptionOptions): UseBookingSubscriptionReturn {
  const [lastBooking, setLastBooking] = useState<BookingTable | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const subscriptionRef = useRef<any>(null)
  const reconnectTimeoutRef = useRef<number>()

  const connect = () => {
    if (!enabled || !id) return

    try {
      // Générer le client Amplify (lazy initialization)
      const client = generateClient()

      // Choisir la subscription selon le type
      const subscription =
        type === 'shop'
          ? onBookingChanged
          : type === 'prestataire'
            ? onBookingChangedForPrestataire
            : onBookingChangedForClient

      const variableKey =
        type === 'shop'
          ? 'shopId'
          : type === 'prestataire'
            ? 'prestatereId'
            : 'clientId'

      console.log(`[AppSync] Subscribing to ${type} booking changes: ${id}`)

      // S'abonner à la subscription
      subscriptionRef.current = client
        .graphql<GraphQLSubscription<{ [key: string]: BookingTable }>>({
          query: subscription,
          variables: { [variableKey]: id },
        })
        .subscribe({
          next: ({ data }) => {
            const booking = data?.[Object.keys(data)[0]] as BookingTable | undefined
            if (booking) {
              console.log(
                `[AppSync] Booking ${booking.bookingId} updated: ${booking.state}`
              )
              setLastBooking(booking)
              setIsConnected(true)
              setError(null)
              onBookingUpdate?.(booking)
            }
          },
          error: (err) => {
            console.error(`[AppSync] Subscription error:`, err)
            const errorObj = err instanceof Error ? err : new Error(String(err))
            setError(errorObj)
            setIsConnected(false)
            onError?.(errorObj)

            // Reconnexion automatique après 5 secondes
            reconnectTimeoutRef.current = window.setTimeout(() => {
              console.log('[AppSync] Attempting to reconnect...')
              connect()
            }, 5000)
          },
          complete: () => {
            console.log('[AppSync] Subscription completed')
            setIsConnected(false)
          },
        })

      setIsConnected(true)
    } catch (err) {
      console.error('[AppSync] Failed to subscribe:', err)
      const errorObj = err instanceof Error ? err : new Error(String(err))
      setError(errorObj)
      setIsConnected(false)
      onError?.(errorObj)
    }
  }

  const disconnect = () => {
    if (subscriptionRef.current) {
      console.log(`[AppSync] Unsubscribing from ${type} booking changes`)
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    setIsConnected(false)
  }

  const reconnect = () => {
    disconnect()
    connect()
  }

  useEffect(() => {
    connect()
    return () => disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id, enabled])

  return {
    lastBooking,
    isConnected,
    error,
    reconnect,
  }
}
